import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Stats } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Calendar, TrendingUp, Activity } from 'lucide-react';

export function Analytics() {
  const [stats24h, setStats24h] = useState<Stats | null>(null);
  const [stats7d, setStats7d] = useState<Stats | null>(null);
  const [stats30d, setStats30d] = useState<Stats | null>(null);
  const [selectedRange, setSelectedRange] = useState<'24h' | '7d' | '30d'>('24h');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const [data24h, data7d, data30d] = await Promise.all([
        api.getStats('24h'),
        api.getStats('7d'),
        api.getStats('30d')
      ]);
      
      setStats24h(data24h);
      setStats7d(data7d);
      setStats30d(data30d);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentStats = () => {
    switch (selectedRange) {
      case '24h': return stats24h;
      case '7d': return stats7d;
      case '30d': return stats30d;
      default: return stats24h;
    }
  };

  const currentStats = getCurrentStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Analytics</h2>
        <div className="flex items-center space-x-2">
          <Calendar className="h-5 w-5 text-gray-400" />
          <select
            value={selectedRange}
            onChange={(e) => setSelectedRange(e.target.value as '24h' | '7d' | '30d')}
            className="rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Activity className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Posts</p>
              <p className="text-2xl font-bold text-gray-900">
                {currentStats?.stats.total_posts?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 font-bold">!</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Critical Threats</p>
              <p className="text-2xl font-bold text-red-600">
                {currentStats?.stats.critical_threats?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center">
                <span className="text-orange-600 font-bold">⚠</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">High Threats</p>
              <p className="text-2xl font-bold text-orange-600">
                {currentStats?.stats.high_threats?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold">👥</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Unique Users</p>
              <p className="text-2xl font-bold text-green-600">
                {currentStats?.stats.unique_users?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Activity Timeline</h3>
          <TrendingUp className="h-5 w-5 text-gray-400" />
        </div>
        <div style={{ width: '100%', height: 400 }}>
          <ResponsiveContainer>
            <AreaChart data={currentStats?.trend_data || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time_bucket" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  if (selectedRange === '24h') {
                    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                  }
                  return date.toLocaleDateString();
                }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                labelFormatter={(value) => {
                  const date = new Date(value);
                  return selectedRange === '24h' 
                    ? date.toLocaleString()
                    : date.toLocaleDateString();
                }}
              />
              <Area 
                type="monotone" 
                dataKey="post_count" 
                stackId="1"
                stroke="#3b82f6" 
                fill="#3b82f6" 
                fillOpacity={0.6}
                name="Total Posts"
              />
              <Area 
                type="monotone" 
                dataKey="threat_count" 
                stackId="2"
                stroke="#ef4444" 
                fill="#ef4444" 
                fillOpacity={0.8}
                name="Threats Detected"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Hourly Distribution (24h only) */}
      {selectedRange === '24h' && currentStats?.trend_data && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Hourly Distribution</h3>
          <div style={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={currentStats.trend_data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="time_bucket" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.getHours().toString().padStart(2, '0') + ':00';
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  labelFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.toLocaleDateString()} ${date.getHours().toString().padStart(2, '0')}:00`;
                  }}
                />
                <Bar dataKey="post_count" fill="#3b82f6" name="Posts" />
                <Bar dataKey="threat_count" fill="#ef4444" name="Threats" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Threat Rate */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Threat Detection Rate</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Threats</span>
              <span className="text-sm font-medium">
                {((currentStats?.stats.critical_threats || 0) + (currentStats?.stats.high_threats || 0)).toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Total Posts</span>
              <span className="text-sm font-medium">
                {currentStats?.stats.total_posts?.toLocaleString() || 0}
              </span>
            </div>
            <div className="border-t pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900">Detection Rate</span>
                <span className="text-lg font-bold text-blue-600">
                  {currentStats?.stats.total_posts ? 
                    (((currentStats.stats.critical_threats + currentStats.stats.high_threats) / currentStats.stats.total_posts) * 100).toFixed(1) + '%'
                    : '0%'
                  }
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Time Period Comparison</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">24 Hours</span>
              <span className="text-sm font-medium">
                {stats24h?.stats.total_posts?.toLocaleString() || 0} posts
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">7 Days</span>
              <span className="text-sm font-medium">
                {stats7d?.stats.total_posts?.toLocaleString() || 0} posts
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">30 Days</span>
              <span className="text-sm font-medium">
                {stats30d?.stats.total_posts?.toLocaleString() || 0} posts
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}